// PWA Utility Functions for offline support and installation

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

/**
 * Initialize PWA install prompt
 */
let deferredPrompt: BeforeInstallPromptEvent | null = null;

export function initInstallPrompt() {
  window.addEventListener("beforeinstallprompt", (e: Event) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
  });

  window.addEventListener("appinstalled", () => {
    console.log("PWA was installed");
    deferredPrompt = null;
  });
}

/**
 * Trigger PWA installation prompt
 */
export function triggerInstallPrompt() {
  if (deferredPrompt) {
    deferredPrompt.prompt();
    deferredPrompt.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === "accepted") {
        console.log("User accepted the install prompt");
      } else {
        console.log("User dismissed the install prompt");
      }
      deferredPrompt = null;
    });
  }
}

/**
 * Check if PWA is already installed
 */
export function isAppInstalled() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as NavigatorWithStandalone).standalone === true
  );
}

/**
 * Store data offline using localStorage
 */
export function saveOfflineData(key: string, data: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error("Failed to save offline data:", error);
    return false;
  }
}

/**
 * Retrieve offline data from localStorage
 */
export function getOfflineData(key: string) {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error("Failed to retrieve offline data:", error);
    return null;
  }
}

/**
 * Clear offline data
 */
export function clearOfflineData(key: string) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error("Failed to clear offline data:", error);
    return false;
  }
}

/**
 * Check if online
 */
export function isOnline() {
  return navigator.onLine;
}

/**
 * Add online/offline listeners
 */
export function addOnlineStatusListener(
  onlineCallback: () => void,
  offlineCallback: () => void
) {
  window.addEventListener("online", onlineCallback);
  window.addEventListener("offline", offlineCallback);

  return () => {
    window.removeEventListener("online", onlineCallback);
    window.removeEventListener("offline", offlineCallback);
  };
}

/**
 * Sync offline changes when back online
 */
export async function syncOfflineData(syncFunction: (data: unknown) => Promise<void>) {
  if (isOnline()) {
    const pendingData = getOfflineData("pending-sync");
    if (pendingData) {
      try {
        await syncFunction(pendingData);
        clearOfflineData("pending-sync");
        return true;
      } catch (error) {
        console.error("Sync failed:", error);
        return false;
      }
    }
  }
  return false;
}

/**
 * Request persistent storage permission (optional)
 */
export async function requestPersistentStorage() {
  if (navigator.storage && navigator.storage.persist) {
    const persistent = await navigator.storage.persist();
    console.log("Persistent storage granted:", persistent);
    return persistent;
  }
  return false;
}

/**
 * Get storage quota and usage
 */
export async function getStorageInfo() {
  if (navigator.storage && navigator.storage.estimate) {
    const estimate = await navigator.storage.estimate();
    const usage = estimate.usage ?? 0;
    const quota = estimate.quota ?? 0;
    return {
      usage,
      quota,
      percentage: quota > 0 ? (usage / quota) * 100 : 0,
    };
  }
  return null;
}
